import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../App';
import { ArrowLeft, Download, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Results = ({ user }) => {
  const navigate = useNavigate();
  const { statementId } = useParams();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadStatement();
    const interval = setInterval(() => {
      if (statement?.status === 'processing') {
        loadStatement();
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [statementId, statement?.status]);

  const loadStatement = async () => {
    try {
      const response = await api.get(`/statements/${statementId}`);
      setStatement(response.data);
    } catch (error) {
      toast.error('Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/statements/${statementId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${statement.filename}_transactions.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('CSV downloaded successfully!');
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ledger-bone flex items-center justify-center">
        <p className="font-mono text-sm text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!statement) {
    return (
      <div className="min-h-screen bg-ledger-bone flex items-center justify-center">
        <p className="font-mono text-sm text-gray-600">Statement not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ledger-bone relative noise-overlay">
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-black/10 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="font-mono text-xs text-gray-600 hover:text-ledger-green flex items-center gap-2"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
            </div>
            <span className="font-mono text-xs text-gray-600" data-testid="user-email">{user?.email}</span>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 py-12">
          {/* Header Info */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="font-serif text-4xl font-bold text-ledger-green mb-2" data-testid="results-title">Results</h1>
              <p className="font-mono text-sm text-gray-600" data-testid="filename">{statement.filename}</p>
            </div>
            {statement.status === 'completed' && (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="bg-ledger-orange text-white rounded-none hover:bg-ledger-orange/90 uppercase tracking-widest text-xs py-4 px-8 font-mono disabled:opacity-50 flex items-center gap-2"
                data-testid="download-csv-btn"
              >
                <Download className="w-4 h-4" />
                {downloading ? 'Downloading...' : 'Download CSV'}
              </button>
            )}
          </div>

          {/* Status */}
          {statement.status === 'processing' && (
            <div className="border-2 border-yellow-500 p-6 bg-yellow-50 mb-8 flex items-center gap-4" data-testid="processing-status">
              <RefreshCw className="w-6 h-6 text-yellow-600 animate-spin" />
              <div>
                <p className="font-mono text-sm text-yellow-900">Processing your statement...</p>
                <p className="font-mono text-xs text-yellow-700 mt-1">This usually takes 10-30 seconds</p>
              </div>
            </div>
          )}

          {statement.status === 'failed' && (
            <div className="border-2 border-red-500 p-6 bg-red-50 mb-8" data-testid="failed-status">
              <p className="font-mono text-sm text-red-900">Processing failed</p>
              {statement.error_message && (
                <p className="font-mono text-xs text-red-700 mt-2">{statement.error_message}</p>
              )}
            </div>
          )}

          {statement.status === 'completed' && (
            <div className="border-2 border-green-500 p-4 bg-green-50 mb-8 flex items-center gap-3" data-testid="completed-status">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="font-mono text-sm text-green-900">
                Extracted {statement.extracted_data?.length || 0} transactions
              </p>
            </div>
          )}

          {/* Transactions Table */}
          {statement.status === 'completed' && statement.extracted_data && statement.extracted_data.length > 0 && (
            <div className="border-2 border-black/10 bg-white p-8">
              <h2 className="font-mono text-sm uppercase tracking-widest text-ledger-green mb-6" data-testid="transactions-title">
                Extracted Transactions
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full font-mono text-sm" data-testid="transactions-table">
                  <thead>
                    <tr className="border-b border-black/10">
                      <th className="text-left py-3 px-4 uppercase tracking-widest text-xs text-gray-500">Date</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest text-xs text-gray-500">Description</th>
                      <th className="text-right py-3 px-4 uppercase tracking-widest text-xs text-gray-500">Amount</th>
                      <th className="text-right py-3 px-4 uppercase tracking-widest text-xs text-gray-500">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.extracted_data.map((transaction, index) => (
                      <tr key={index} className="border-b border-black/10" data-testid={`transaction-row-${index}`}>
                        <td className="py-3 px-4 text-gray-700">{transaction.date}</td>
                        <td className="py-3 px-4 text-gray-700">{transaction.description}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{transaction.amount}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{transaction.balance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          {statement.status === 'completed' && (
            <div className="mt-8 flex gap-4">
              <button
                onClick={() => navigate('/upload')}
                className="bg-ledger-green text-white rounded-none hover:bg-ledger-green/90 uppercase tracking-widest text-xs py-3 px-6 font-mono"
                data-testid="upload-another-btn"
              >
                Upload Another Statement
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Results;